import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getV2InternetProduct } from "../../../../shared/hooks/useFetchServiceDetails";
import API_URLS from "../../Utils/axr";
import store from "../store";
import DwLogutil from "../../Utils/DwLogutil";
import { errorConditionGTM } from "../../Utils/googleTags";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";

type MoversInternetProductStateType = {
  moversInternetProductData: any;
  internetAPILoading: boolean;
  error: any;
};

const initialState: MoversInternetProductStateType = {
  moversInternetProductData: null,
  internetAPILoading: false,
  error: null,
};

// Async thunk
export const getMoversInternetProduct = createAsyncThunk(
  "moversInternetProduct/fetch",
  async (addressQualificationData: any, { rejectWithValue }) => {
    try {
      const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const user = reduxStore?.user?.data;
      const moversAddressQualificationData = addressQualificationData?.entity;

      const siteId =
        reduxStore?.addressQualification?.data?.addressQualification?.entity?.siteId ||
        headers?.siteId?.toLowerCase();

      const serviceDetails= reduxStore?.serviceDetails?.serviceDetails;
      let productId:any;
      if(serviceDetails?.subscriptions?.length > 0) {
        for (let index = 0; index < serviceDetails?.subscriptions[0]?.serviceDetails.length; index++) {
            const element = serviceDetails?.subscriptions[0]?.serviceDetails[index];
            if( element?.serviceType?.includes("Internet")) {
             productId =(element?.serviceModel)
            }
          }
        }
      let serviceModelProductId = productId?.substring(0, productId.length - 1);
      
      const planDetails = reduxStore?.planDetails?.planDetails;                    
      const affinityDiscountApplicable = planDetails && planDetails?.discountPlans?.find((affinityApplicable:any) => affinityApplicable?.productName?.includes("Affinity Discount"));
      let zuoraRatePlanId = "";
      let currentPlanPrice = "";
      let speed = "";
      if (planDetails?.standardPlans?.length > 0) {
        for (
          let index = 0;
          index < planDetails?.standardPlans?.length;
          index++
        ) {
          const element = planDetails?.standardPlans[index];
          if (element?.productName?.toLowerCase()?.includes("internet")) {
            zuoraRatePlanId = element?.productRatePlanId;
            currentPlanPrice = element?.planPrice;
            speed= element?.serviceLevel?.toLowerCase()?.includes(["8g","5g","2g","1g"]) &&  element?.serviceLevel?.split("g")?.[0] + "000" || "";
          }
        }
      }
      
      const affinityOccupation = (affinityDiscountApplicable && affinityDiscountApplicable?.affinityOccupation) ;

      const payload = {
        baseline: {
          transactionId: moversAddressQualificationData?.partnerSessionId ||
            reduxStore?.addressQualification?.data?.addressQualification?.entity?.partnerSessionId,
          channel: "web-acm",
          wirecenter: moversAddressQualificationData?.wireCenter || 
            reduxStore?.addressQualification?.data?.addressQualification?.entity?.wireCenter,
          siteId: moversAddressQualificationData?.siteId,
          workflow: "mover",
          billingType: "postpaid",
        },
        productsRequest: {
          marketSegment: "",
          qualForExternalVideo: false,
          customerType: null,
          productId: serviceModelProductId,
          instantInternet: moversAddressQualificationData?.instantInternet,
          fullAddress: moversAddressQualificationData?.fullAddress,
          speed: moversAddressQualificationData?.qualificationDetails?.maxUpBandwidth,
          provisioningCode: "",
          businessName: "",
          mmmCode: moversAddressQualificationData?.marketingProfile?.mmmCode,
          uploadSpeed: moversAddressQualificationData?.qualificationDetails?.maxUpBandwidth,
          downloadSpeed: moversAddressQualificationData?.qualificationDetails?.maxDownBandwidth,
          cpeTechType: moversAddressQualificationData?.qualificationDetails?.cpeTechType,
          customerGroupType: affinityOccupation,
          catalogueLevers: false,
          propertyId: moversAddressQualificationData?.propertyId,
          productRatePlanId: null,
          ratePlanId: zuoraRatePlanId,
          fiberVoiceEligible: moversAddressQualificationData?.fiberVoiceEligible,
          fourglteQualifiedProductType: null,
          postpaidFiberEligible: true,
          existingSpeed: null,
          hasAutopayOrPmtDiscount: false,
        },
        allowSpeedUpgradeOnly:false
      };

      const params = { "": [API_URLS?.getV2InternetProduct] };

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_internet_product_api_triggered });

      const response = await getV2InternetProduct(params, payload, headers);

      DwLogutil({
        stepName:
          Number(response?.data?.messageStatus?.[0]?.statusCode) === 200
            ? EC_DW_LOGGING_STEPS.movers_internet_product_api_success
            : EC_DW_LOGGING_STEPS.movers_internet_product_api_failed,
        error:
          Number(response?.data?.messageStatus?.[0]?.statusCode) !== 200
            ? response?.data?.messageStatus?.[0]?.statusMessage
            : undefined,
      });

      return response?.data || response;
    } catch (error: any) {
      const errorMessage =
        error?.message || "Something went wrong while fetching Movers Internet Product";

      DwLogutil({
        stepName: EC_DW_LOGGING_STEPS.movers_internet_product_api_failed,
        error: errorMessage,
      });

      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const moversInternetProductSlice = createSlice({
  name: "moversInternetProduct",
  initialState,
  reducers: {
    clearMoversInternetProductSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMoversInternetProduct.pending, (state) => {
        state.internetAPILoading = true;
        state.error = null;
      })
      .addCase(
        getMoversInternetProduct.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.internetAPILoading = false;
          state.moversInternetProductData = action.payload;
          state.error = null;
        }
      )
      .addCase(getMoversInternetProduct.rejected, (state, action) => {
        state.internetAPILoading = false;
        state.moversInternetProductData = null;
        state.error = action.payload;

        errorConditionGTM(
          "BRSPD_Fiber_EC_Flow",
          "movers internet product",
          action.error?.message,
          action?.type
        );
      });
  },
});

export const { clearMoversInternetProductSlice } = moversInternetProductSlice.actions;
export default moversInternetProductSlice.reducer;